import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Modal,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterDocumentsService } from "../../../../shared/services/api/sinister/SinisterDocumentsService";
import { globalTranslate } from "../../../../shared/utils";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";
import { ModalAddImageValue } from "./ModalAddImageValue";
import { ModalDocumentValueList } from "./ModalDocumentValueList";
import { ModalPreviewValue } from "./ModalPreviewValue";

interface ModalDocumentoValuesProps {
	open: boolean;
	rowSelect: any;
	onClose: () => void;
	onSuccess?: (refresh?: boolean) => void;
}

export const ModalDocumentValue = ({ open, rowSelect, onClose, onSuccess }: ModalDocumentoValuesProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [previewDoc, setPreviewDoc] = useState<any | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [documentToDelete, setDocumentToDelete] = useState<any | null>(null);
	const [documentTypes, setDocumentTypes] = useState<any[]>([]);
	const toastHandler = useToast();
	const documents = rowSelect?.sinisterValuesDocuments || [];
	const allowedStatuses = ["open", "contested"];
	const canUpload = allowedStatuses.includes(rowSelect?.status);
	const canDelete = rowSelect?.status === "open";

	useEffect(() => {
		if (rowSelect?.sinisterId && rowSelect?.id) {
			setIsLoading(true);
			SinisterDocumentsService.getSinisterValuesDocumentTypes(rowSelect.sinisterId, rowSelect.id)
				.then((result) => {
					setDocumentTypes(result);
				})
				.catch(() => {
					toastHandler.present({
						type: "error",
						messageOrContent: "Erro ao carregar os tipos de documentos esperados!",
						position: "top-right",
					});
				})
				.finally(() => setIsLoading(false));
		}
	}, [open]);

	const confirmDeleteDocument = (document: any) => {
		setDocumentToDelete(document);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirmed = async () => {
		if (!documentToDelete) return;

		setDeleteDialogOpen(false);
		setIsLoading(true);
		onSuccess?.(false);

		SinisterDocumentsService.removeDocumentBySinister(documentToDelete.id)
			.then((result: void | Error) => {
				setIsLoading(false);
				onSuccess?.(true);

				if (result instanceof Error) {
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title="Alerta!!" content={`Erro: ${result.message}`} />,
					});
				} else {
					toastHandler.present({
						type: "success",
						position: "top-right",
						durationMs: 3000,
						messageOrContent: <ToastContent title="SUCESSO!" content="Documento excluído com sucesso." />,
					});

					setTimeout(() => {
						onSuccess?.(false);
					}, 500);
				}
			})
			.catch((err: any) => {
				setIsLoading(false);
				const { titleMessage, contentMessage } = returnMessageError(err);

				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			});
	};

	const handleOpenPreview = (doc: any) => {
		setIsLoading(true);
		setPreviewDoc(doc);

		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}

		SinisterDocumentsService.previewDocumentBySinisterValues(doc.id).then((result) => {
			setIsLoading(false);

			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					messageOrContent: "Erro ao visualizar o documento!",
					position: "top-center",
				});
				setPreviewDoc(null);
			} else {
				const blobUrl = URL.createObjectURL(result);
				setPreviewUrl(blobUrl);
			}
		});
	};

	const handleClosePreview = () => {
		setPreviewDoc(null);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}
	};

	return (
		<>
			<Modal open={open} onClose={onClose}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						width: "95%",
						maxWidth: 720,
						maxHeight: "90vh",
						overflowY: "auto",
						borderRadius: 3,
					}}
				>
					<LoadingComp isopen={isLoading} />

					<Grid container justifyContent="space-between" alignItems="center">
						<Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
							Documentos da cobrança - {`${globalTranslate(rowSelect?.typeId ?? "")} ${rowSelect?.reference ?? ""}`}
						</Typography>

						<IconButton onClick={onClose} size="small">
							<CloseIcon />
						</IconButton>
					</Grid>

					<Divider sx={{ my: 2 }} />

					<Stack spacing={2}>
						<ModalDocumentValueList
							documents={documents}
							onPreview={handleOpenPreview}
							onDelete={confirmDeleteDocument}
							documentTypes={documentTypes}
							canDelete={canDelete}
						/>

						{canUpload && (
							<ModalAddImageValue
								sinisterId={rowSelect.sinisterId}
								checkListId={rowSelect.id}
								documentTypeId="otherAtachments" // ou o tipo padrão que desejar
								onSuccessUpload={() => onSuccess?.(true)}
							/>
						)}
					</Stack>
				</Box>
			</Modal>

			<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
				<DialogTitle>Confirmar exclusão</DialogTitle>
				<DialogContent>
					<DialogContentText>Tem certeza que deseja excluir o anexo "{documentToDelete?.originalName}"?</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} color="inherit">
						Cancelar
					</Button>
					<Button onClick={handleDeleteConfirmed} color="error" variant="contained">
						Excluir
					</Button>
				</DialogActions>
			</Dialog>

			<ModalPreviewValue open={!!previewDoc} onClose={handleClosePreview} previewDoc={previewDoc} previewUrl={previewUrl} isLoading={isLoading} />
		</>
	);
};
